import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { initials, Modal } from '../ui/Kit';

// A candidate in a list (bundle 4d — Candidates database and Find candidates): a compact 1–2 line row that expands on
// hover, keyboard focus or tap. Only one row is open at a time (the list holds openId); the height animates (no layout
// jump of the collapsed rows) and respects prefers-reduced-motion. Right end: the jobs they're already on, as chips.

const CHIP = {
  out: 'bg-[#FEE2E2] text-[#991B1B]', joined: 'bg-[#DCFCE7] text-[#166534]', shortlisted: 'bg-[#E0F2FE] text-[#075985]',
  pending: 'bg-[#F1F5F9] text-[#334155]',
};
const tone = (j) => (['rejected', 'withdrawn'].includes(j.state) ? CHIP.out : CHIP[j.state] || CHIP.pending);

function Chip({ j }) {
  const navigate = useNavigate();
  return (
    <button type="button" onClick={(e) => { e.stopPropagation(); navigate(j.link); }} title={j.text}
      className={`max-w-[230px] truncate px-2 py-[3px] rounded-md text-[11px] font-semibold ${tone(j)} ${j.current ? 'ring-2 ring-[#0066CC]' : ''}`}
      data-testid="job-chip" data-current={j.current ? 'true' : 'false'}>{j.text}</button>
  );
}

export function JobChips({ jobs = [], hidden = 0, max = 2 }) {
  const [all, setAll] = useState(false);
  if (!jobs.length && !hidden) return null;
  const more = jobs.length - max;
  return (
    <span className="flex flex-wrap justify-end gap-1" data-testid="job-chips">
      {jobs.slice(0, max).map((j) => <Chip key={j.application_id} j={j} />)}
      {more > 0 && <button type="button" className="px-2 py-[3px] rounded-md text-[11px] font-semibold text-[#0066CC] hover:underline"
        onClick={(e) => { e.stopPropagation(); setAll(true); }} data-testid="job-chips-more">+{more} more</button>}
      {hidden > 0 && <span className="px-2 py-[3px] rounded-md text-[11px] text-[#64748B]" data-testid="job-chips-hidden">{hidden} other job{hidden > 1 ? 's' : ''} (no access)</span>}
      {all && (                                   // inside the row: its clicks / focus mustn't open or close the row
        <span onClick={(e) => e.stopPropagation()} onFocus={(e) => e.stopPropagation()} onBlur={(e) => e.stopPropagation()} role="presentation">
          <Modal open title="Jobs" onClose={() => setAll(false)} width="max-w-md">
            <ul className="space-y-1.5" data-testid="job-chips-all">{jobs.map((j) => <li key={j.application_id}><Chip j={j} /></li>)}</ul>
          </Modal>
        </span>
      )}
    </span>
  );
}

export function Initials({ name, risk }) {
  const dot = { low: 'bg-[#22C55E]', review: 'bg-[#F59E0B]', high: 'bg-[#DC2626]' }[risk];
  return (
    <span className="relative inline-flex w-10 h-10 shrink-0 rounded-full bg-[#E8F1FB] text-[#003D82] text-sm font-bold items-center justify-center" aria-hidden="true">
      {initials(name)}
      {dot && <span className={`absolute -right-0.5 -bottom-0.5 w-3 h-3 rounded-full border-2 border-white ${dot}`} data-testid="risk-dot" />}
    </span>
  );
}

export default function CandidateRow({ id, open, onOpen, onClose, lead, name, link, subtitle, jobs, hidden, children, testid = 'cand-row' }) {
  const navigate = useNavigate();
  const toggleTap = (e) => {
    if (e.target.closest('button, a, input, label, select')) return;
    if (open) onClose(id); else onOpen(id);
  };
  return (
    <li className={`group relative border-b border-[#EEF1F6] last:border-0 ${open ? 'bg-[#F8FAFC]' : ''}`} data-testid={testid} data-open={open ? 'true' : 'false'}
      tabIndex={0} aria-label={name}
      onMouseEnter={() => onOpen(id)} onMouseLeave={() => onClose(id)} onClick={toggleTap}
      onFocus={() => onOpen(id)} onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) onClose(id); }}>
      <div className="flex items-center gap-3 px-4 py-2.5 min-h-[56px]">
        {lead}
        <div className="min-w-0 flex-1">
          <button type="button" className="font-bold text-[#0F172A] hover:text-[#0066CC] text-left truncate max-w-full" onClick={() => navigate(link)}>{name}</button>
          <p className="text-xs text-[#64748B] truncate">{subtitle}</p>
        </div>
        <div className="hidden sm:block max-w-[48%]"><JobChips jobs={jobs} hidden={hidden} /></div>
      </div>
      <div className="grid transition-[grid-template-rows] duration-200 ease-out motion-reduce:transition-none" style={{ gridTemplateRows: open ? '1fr' : '0fr' }}>
        <div className="overflow-hidden" aria-hidden={!open}>
          <div className="px-4 pb-3 pt-0.5 sm:pl-[68px]" data-testid={`${testid}-details`}>
            <div className="sm:hidden mb-2"><JobChips jobs={jobs} hidden={hidden} /></div>
            {open && children}
          </div>
        </div>
      </div>
    </li>
  );
}
